import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import cytoscape, { Core, ElementDefinition } from 'cytoscape';
import cytoscapeLayers, { LayersPlugin } from 'cytoscape-layers';
import cytoscapeOverlays from 'cytoscape-overlays';
import fcose from 'cytoscape-fcose';
import layoutUtilities from 'cytoscape-layout-utilities';
import type { PositionMap } from '../utils/graphPositions';

// cytoscape-overlays declares itself ESM but ships CommonJS (`exports.default = register`),
// and Vite 8's interop hands a default import of that the whole exports object. Take the
// function either way, so a corrected package keeps working too.
const registerOverlays: cytoscape.Ext =
  typeof cytoscapeOverlays === 'function'
    ? cytoscapeOverlays
    : (cytoscapeOverlays as { default: cytoscape.Ext }).default;

// Register Cytoscape extensions (only once). layout-utilities must be
// registered for fcose's packComponents to work — without it fcose silently
// skips packing and disconnected components stack on top of each other.
cytoscape.use(cytoscapeLayers);
cytoscape.use(registerOverlays);
cytoscape.use(fcose);
cytoscape.use(layoutUtilities);

// Define stylesheet type based on Cytoscape's StylesheetStyle interface
interface StylesheetRule {
  selector: string;
  style: Record<string, unknown>;
}

/**
 * Public API exposed via ref for layer management and position control
 */
export interface NetworkGraphHandle {
  /** Get the Cytoscape core instance */
  getCy: () => Core | null;
  /** Get the layers plugin instance */
  getLayers: () => LayersPlugin | null;
  /** Get current node positions */
  getPositions: () => PositionMap;
}

interface NetworkGraphProps {
  elements?: ElementDefinition[];
  stylesheet?: StylesheetRule[];
  layout?: cytoscape.LayoutOptions;
  style?: React.CSSProperties;
  onNodeClick?: (nodeId: string, nodeData: unknown) => void;
  onEdgeClick?: (edgeId: string, edgeData: unknown) => void;
  /**
   * Called when the user taps empty canvas (anywhere that's not a
   * node or edge). Lets the parent clear its selection state — e.g.
   * close the device detail panel and drop the device URL param.
   * Without this, clicking the background does nothing visible
   * because Cytoscape's internal selection deselect never reaches
   * React.
   */
  onBackgroundClick?: () => void;
  /** Called when a node is dragged to a new position, receives the dragged node's id */
  onNodeDragEnd?: (nodeId: string) => void;
  /**
   * Called with the live Cytoscape instance whenever it is (re)created, and
   * with null when it's destroyed. Parents that register cy event handlers
   * in their own effects MUST depend on this value (keep it in state) rather
   * than grabbing `getCy()` from the ref imperatively: StrictMode's dev-only
   * unmount/remount of a newly mounted NetworkGraph destroys and re-creates
   * the instance WITHOUT re-running the parent's effects, silently leaving
   * any handlers bound to the destroyed instance. (Found the hard way — on a
   * clean page load the endpoints-overlay drag handlers died this way, while
   * cached-data navigations mounted parent and graph in the same commit and
   * happened to survive.)
   */
  onCyChanged?: (cy: Core | null) => void;
  /** ID of the node to show as selected (synced with URL state) */
  selectedNodeId?: string | null;
  /** ID of the edge to show as selected (synced with URL state) */
  selectedEdgeId?: string | null;
  /**
   * Resolved UI theme. Drives the default stylesheet's label/edge/border
   * colors so the graph is legible on a dark background. Ignored when a
   * custom `stylesheet` is supplied. Defaults to light.
   */
  theme?: 'light' | 'dark';
}

/**
 * NetworkGraph - A React wrapper component for Cytoscape.js
 *
 * This component provides a declarative interface for rendering network graphs using Cytoscape.js.
 * It handles initialization, cleanup, and updates of the graph visualization.
 *
 * Best Practices Followed:
 * - Uses refs to manage Cytoscape instance and DOM container
 * - Proper cleanup on unmount to prevent memory leaks
 * - Declarative props interface for React developers
 * - TypeScript types for type safety
 * - Exposes layer management via ref handle
 *
 * @param elements - Array of nodes and edges to render
 * @param stylesheet - Cytoscape styling rules
 * @param layout - Layout algorithm configuration
 * @param style - CSS styles for the container div
 */
const NetworkGraph = forwardRef<NetworkGraphHandle, NetworkGraphProps>(function NetworkGraph({
  elements = [],
  stylesheet,
  layout = { name: 'cose' }, // Default to force-directed layout
  style = { width: '100%', height: '600px' },
  onNodeClick,
  onEdgeClick,
  onBackgroundClick,
  onNodeDragEnd,
  onCyChanged,
  selectedNodeId,
  selectedEdgeId,
  theme = 'light',
}, ref) {
  // Ref to hold the DOM container for Cytoscape
  const containerRef = useRef<HTMLDivElement>(null);

  // Ref to hold the Cytoscape instance
  const cyRef = useRef<Core | null>(null);

  // Ref to hold the layers plugin instance
  const layersRef = useRef<LayersPlugin | null>(null);

  // Latest onCyChanged, so the init effect (empty deps, runs once per
  // mount/StrictMode cycle) always notifies the current callback.
  const onCyChangedRef = useRef(onCyChanged);
  onCyChangedRef.current = onCyChanged;

  // Helper to extract current positions
  const getPositions = (): PositionMap => {
    if (!cyRef.current) return {};
    const positions: PositionMap = {};
    cyRef.current.nodes().forEach((node) => {
      const pos = node.position();
      positions[node.id()] = { x: pos.x, y: pos.y };
    });
    return positions;
  };

  // Expose imperative handle for parent components to access cy, layers, and positions
  useImperativeHandle(ref, () => ({
    getCy: () => cyRef.current,
    getLayers: () => layersRef.current,
    getPositions,
  }), []);

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize Cytoscape instance
    cyRef.current = cytoscape({
      container: containerRef.current,
      elements: elements,
      style: stylesheet || getDefaultStylesheet(theme),
      layout: layout,
      // Enable zoom and pan
      minZoom: 0.1,
      maxZoom: 10,
      wheelSensitivity: 1.0,
    });

    // Initialize the layers plugin
    // The layers() method is added by the cytoscape-layers extension
    layersRef.current = (cyRef.current as Core & { layers: () => LayersPlugin }).layers();

    // Announce the live instance (see onCyChanged prop docs — parents must
    // treat this as reactive state, not read the ref imperatively).
    onCyChangedRef.current?.(cyRef.current);

    // Cytoscape caches the container's on-screen offset to translate pointer
    // coords into model coords for hit-testing. If the container *moves*
    // without changing its own size — e.g. a global banner appears above the
    // scroll area and pushes the fixed-height graph down — that cache goes
    // stale and clicks land offset by the shift. The container itself doesn't
    // resize, so observing it isn't enough; we also watch the nearest
    // scrolling ancestor (the <main> region), which does resize when a banner
    // is inserted, and recompute on either change. Bonus: this also fixes the
    // same offset after a window resize.
    const recompute = () => { cyRef.current?.resize(); };
    const resizeObserver = new ResizeObserver(recompute);
    resizeObserver.observe(containerRef.current);
    const scrollParent = findScrollParent(containerRef.current);
    if (scrollParent) resizeObserver.observe(scrollParent);

    // Cleanup function - destroy Cytoscape instance on unmount
    return () => {
      onCyChangedRef.current?.(null);
      resizeObserver.disconnect();
      layersRef.current = null;
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only initialize once, updates handled by separate effects below

  // Add event listeners for node and edge clicks
  useEffect(() => {
    if (!cyRef.current) return;

    const cy = cyRef.current;

    // Node click handler
    const handleNodeTap = (event: cytoscape.EventObject) => {
      if (onNodeClick) {
        const node = event.target as cytoscape.NodeSingular;
        onNodeClick(node.id(), node.data());
      }
    };

    // Edge click handler
    const handleEdgeTap = (event: cytoscape.EventObject) => {
      if (onEdgeClick) {
        const edge = event.target as cytoscape.EdgeSingular;
        onEdgeClick(edge.id(), edge.data());
      }
    };

    // Node drag end handler for position persistence
    const handleDragFree = (event: cytoscape.EventObject) => {
      if (onNodeDragEnd) {
        onNodeDragEnd((event.target as cytoscape.NodeSingular).id());
      }
    };

    // Background tap handler — fires when the user taps anywhere
    // that isn't a node or edge. Cytoscape sets `event.target` to
    // the core (cy) itself in that case. Lets the parent clear
    // panel state so deselecting on the canvas behaves the same as
    // closing the panel.
    const handleBackgroundTap = (event: cytoscape.EventObject) => {
      if (event.target === cy && onBackgroundClick) {
        onBackgroundClick();
      }
    };

    // Register event listeners
    cy.on('tap', 'node', handleNodeTap);
    cy.on('tap', 'edge', handleEdgeTap);
    cy.on('tap', handleBackgroundTap);
    cy.on('dragfree', 'node', handleDragFree);

    // Cleanup event listeners
    return () => {
      cy.off('tap', 'node', handleNodeTap);
      cy.off('tap', 'edge', handleEdgeTap);
      cy.off('tap', handleBackgroundTap);
      cy.off('dragfree', 'node', handleDragFree);
    };
  }, [onNodeClick, onEdgeClick, onBackgroundClick, onNodeDragEnd]);

  // Update elements when they change
  useEffect(() => {
    if (!cyRef.current) return;

    // Remove all existing elements
    cyRef.current.elements().remove();

    // Add new elements
    cyRef.current.add(elements);

    // Re-run layout without fit: initial fit happens in the cytoscape constructor at mount;
    // in-session element updates (e.g. websocket-driven topology refreshes) must not re-fit,
    // or they yank the user's zoom/pan. The cast is needed because `fit` doesn't exist on
    // every member of the LayoutOptions union (e.g. NullLayoutOptions); layouts without it
    // simply ignore the extra key.
    cyRef.current.layout({ ...layout, fit: false } as cytoscape.LayoutOptions).run();
  }, [elements, layout]);

  // Update stylesheet when it (or the theme) changes
  useEffect(() => {
    if (!cyRef.current) return;

    cyRef.current.style(stylesheet || getDefaultStylesheet(theme));
  }, [stylesheet, theme]);

  // Sync selection state with Cytoscape (for URL-driven selection)
  useEffect(() => {
    if (!cyRef.current) return;

    const cy = cyRef.current;

    // Clear all selections first
    cy.elements().unselect();

    // Select the specified node if it exists
    if (selectedNodeId) {
      const node = cy.getElementById(selectedNodeId);
      if (node.length > 0) {
        node.select();
      }
    }

    // Select the specified edge if it exists
    if (selectedEdgeId) {
      const edge = cy.getElementById(selectedEdgeId);
      if (edge.length > 0) {
        edge.select();
      }
    }
  }, [selectedNodeId, selectedEdgeId, elements]); // Re-run when elements change in case selected item is added

  return (
    <div
      ref={containerRef}
      style={style}
      className="network-graph-container"
    />
  );
});

export default NetworkGraph;

/**
 * Walk up the DOM from `el` to the first ancestor that scrolls vertically.
 * That element resizes when content above it (a banner) is inserted, which is
 * the signal we need to recompute Cytoscape's container offset. Returns null
 * if none is found (then we only watch the container itself).
 */
function findScrollParent(el: HTMLElement): HTMLElement | null {
  let node = el.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Default stylesheet for the network graph
 * Provides sensible defaults for nodes and edges
 */
function getDefaultStylesheet(theme: 'light' | 'dark' = 'light'): StylesheetRule[] {
  const dark = theme === 'dark';
  // Theme-dependent chrome colors. Node fills (blue/grey) and the status
  // rings read fine on either background, so only the label text, edges, and
  // node halo border flip with the theme.
  const labelColor = dark ? '#e5e7eb' : '#363636';
  const edgeColor = dark ? '#4b5563' : '#dbdbdb';
  const nodeBorderColor = dark ? '#1f2937' : '#fff';
  // Endpoints overlay — end-user satellite nodes/edges. Deliberately neutral
  // (a later increment colors wireless edges by band on the Physical lens).
  const endpointFill = dark ? '#475569' : '#94a3b8';
  const endpointLabelColor = dark ? '#94a3b8' : '#64748b';
  const endpointEdgeColor = dark ? '#334155' : '#e2e8f0';
  return [
    {
      // Default node style - uses icon if available
      selector: 'node',
      style: {
        'background-color': 'transparent',
        'background-image': 'data(iconUrl)',
        'background-fit': 'cover',
        'background-clip': 'node',
        'label': 'data(label)',
        'color': labelColor,
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 8,
        'font-size': '12px',
        'width': '50px',
        'height': '50px',
        'border-width': '0px',
        'border-color': nodeBorderColor,
      },
    },
    {
      // Monitored/activated devices get blue background and glow
      selector: 'node[isMonitored]',
      style: {
        'background-color': '#a0c8ff',
        'underlay-color': '#3273dc',
        'underlay-padding': '5px',
        'underlay-opacity': 0.3,
        'underlay-shape': 'ellipse',
      },
    },
    {
      // Discovered/non-monitored devices get gray background and glow
      selector: 'node[!isMonitored]',
      style: {
        'background-color': '#b5b5b5',
        'underlay-color': '#7a7a7a',
        'underlay-padding': '5px',
        'underlay-opacity': 0.25,
        'underlay-shape': 'ellipse',
      },
    },
    {
      // Failed devices get red underlay ring (overrides blue/gray)
      selector: 'node[status="failed"]',
      style: {
        'underlay-color': '#f14668',
        'underlay-padding': '9px',
        'underlay-opacity': 0.4,
        'underlay-shape': 'ellipse',
      },
    },
    {
      // Offline devices also get red underlay ring (overrides blue/gray)
      selector: 'node[status="offline"]',
      style: {
        'underlay-color': '#f14668',
        'underlay-padding': '9px',
        'underlay-opacity': 0.4,
        'underlay-shape': 'ellipse',
      },
    },
    {
      // `unknown` devices get a gray ring: nobody is currently in a position to
      // say whether they are alive. Not red — that would claim a fault we have
      // no evidence for — but not the plain monitored blue either, which would
      // claim a health we equally have no evidence for.
      selector: 'node[status="unknown"]',
      style: {
        'underlay-color': '#7a7a7a',
        'underlay-padding': '9px',
        'underlay-opacity': 0.4,
        'underlay-shape': 'ellipse',
      },
    },
    {
      selector: 'edge',
      style: {
        'width': 2,
        'line-color': edgeColor,
        'curve-style': 'bezier',
      },
    },
    {
      selector: 'node:selected',
      style: {
        'underlay-color': '#48c774',
        'underlay-padding': '7px',
        'underlay-opacity': 0.5,
        'underlay-shape': 'ellipse',
      },
    },
    {
      selector: 'edge:selected',
      style: {
        'line-color': '#48c774',
        'width': 5,
      },
    },
    {
      // Endpoint satellite nodes (end-user devices). No `iconUrl` is set for
      // these, so explicitly clear the base rule's `background-image` (else
      // `data(iconUrl)` resolving to undefined can leave a blank/broken node)
      // and paint a plain muted circle instead. `underlay-opacity: 0` also
      // suppresses the monitored/discovered halo the base node rules add
      // (endpoints don't carry `isMonitored`, so `node[!isMonitored]` would
      // otherwise match and draw a device-style glow around a 20px dot).
      selector: 'node[isEndpoint]',
      style: {
        'background-image': 'none',
        'background-color': endpointFill,
        'underlay-opacity': 0,
        'shape': 'ellipse',
        'width': 20,
        'height': 20,
        'label': 'data(label)',
        'color': endpointLabelColor,
        'font-size': '9px',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 4,
        'min-zoomed-font-size': 10,
        'border-width': '0px',
      },
    },
    {
      // "+N more" aggregate node for endpoints past the per-device cap.
      selector: 'node[isEndpointOverflow]',
      style: {
        'background-image': 'none',
        'background-color': endpointFill,
        'underlay-opacity': 0,
        'shape': 'round-rectangle',
        // Auto-fit the pill to its label text instead of a fixed width.
        'width': 'label',
        'height': 20,
        'padding': '6px',
        'label': 'data(label)',
        'color': endpointLabelColor,
        'font-size': '9px',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 4,
        'min-zoomed-font-size': 10,
        'border-width': '0px',
      },
    },
    {
      selector: 'edge[endpointLink]',
      style: {
        'width': 1,
        'curve-style': 'straight',
        'line-color': endpointEdgeColor,
        'target-arrow-shape': 'none',
      },
    },
    {
      selector: 'edge[endpointLink = "wireless"]',
      style: {
        'line-style': 'dashed',
        'line-dash-pattern': [6, 3],
      },
    },
  ];
}
